/**
 * customers.ts — TURNING WHAT THE SERVER SAID INTO A SENTENCE, WITHOUT EVER
 * TURNING SILENCE INTO A NUMBER.
 *
 * Five answers, kept apart, because the four the lane contract names are not
 * quite enough on a surface where half the reads have not been built yet:
 *
 *   MEASURED   the server answered and this is the answer. Includes a real 0.
 *   ZERO       a measured zero, rendered as a measurement rather than a blank.
 *              (Carried by MEASURED — the point is that it is never a default.)
 *   ABSENT     the server answered and there is no such record.
 *   WITHHELD   the server holds it and policy forbids showing it. The
 *              invitation token is the live example: it exists, it is not in
 *              the reply, and "no link" would be a lie.
 *   REFUSED    the caller may not ask. A 403 from a credential scoped to a
 *              different platform authority is this, and it is NOT zero.
 *   UNSERVED   nobody built the read. Not a refusal, not an absence, not zero —
 *              the product cannot answer the question at all yet.
 *
 * Every function here is pure and takes a server payload. There is no store,
 * no cache and no derivation that could outlive a response.
 */
import type { ConsoleOutcome } from './client.js';
import type { PlatformRead, ServerOnboarding } from './tenancy.js';

export type Measure<T> =
  | { readonly kind: 'measured'; readonly value: T }
  | { readonly kind: 'absent'; readonly why: string }
  | { readonly kind: 'withheld'; readonly why: string }
  | { readonly kind: 'refused'; readonly why: string }
  /**
   * THE READ DID NOT COMPLETE. Distinct from every other answer here and the
   * one the lane contract names by name: "0 for we didn't look is a bug even
   * when the number renders fine". A transport failure and a malformed request
   * both land here, because in neither case did anybody measure anything.
   */
  | { readonly kind: 'unread'; readonly why: string }
  | { readonly kind: 'unserved'; readonly read: PlatformRead };

export function measured<T>(value: T): Measure<T> {
  return { kind: 'measured', value };
}

export function unserved<T>(read: PlatformRead): Measure<T> {
  return { kind: 'unserved', read };
}

/**
 * A FAILED READ, TURNED INTO THE RIGHT KIND OF SILENCE.
 *
 * Every branch here is a place a console could have rendered a zero, an empty
 * list or a blank, and each one would have meant something different from the
 * others. The server's own sentence is carried through verbatim, because on
 * this surface the refusal IS the answer — a 403 from a credential scoped to
 * another platform authority names the wall, and paraphrasing it deletes the
 * only part that tells the operator what to do next.
 */
export function failureMeasure(
  outcome: Exclude<ConsoleOutcome<unknown>, { kind: 'ok' }>,
): Measure<never> {
  switch (outcome.kind) {
    case 'absent':
      return { kind: 'absent', why: outcome.message };
    case 'refused':
      return { kind: 'refused', why: `(${outcome.status}) ${outcome.message}` };
    case 'unauthenticated':
      return {
        kind: 'refused',
        why: `the platform credential was not accepted. ${outcome.message}`,
      };
    case 'invalid':
      return { kind: 'unread', why: `the platform API rejected the request. ${outcome.message}` };
    case 'transport':
      return { kind: 'unread', why: outcome.message };
  }
}

/**
 * The sentence for each answer. Six distinct sentences and not one of them is
 * "0", "—" or "". `console-refused-is-not-zero.test.ts` holds that.
 */
export function describeMeasure(m: Measure<unknown>): string {
  switch (m.kind) {
    case 'measured':
      return String(m.value);
    case 'absent':
      return `Not present — ${m.why}`;
    case 'withheld':
      return `Withheld — ${m.why}`;
    case 'refused':
      return `Refused — ${m.why}`;
    case 'unread':
      return `Not measured — ${m.why}`;
    case 'unserved':
      return (
        `No platform read serves this. ${m.read.method} ${m.read.route} is not registered. ` +
        `Held by: ${m.read.heldBy}`
      );
  }
}

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

/**
 * "n / total", WHERE THE TOTAL IS ALWAYS THE SERVER'S NUMBER.
 *
 * Fourteen is real — it is `ONBOARDING_STEPS.length` in
 * `apps/api/src/domains/onboarding/schemas.ts` — and this console is still not
 * allowed to know it. Writing `14` here, or a copy of the fourteen step names,
 * IS the parallel checklist the architecture forbids: a console holding its own
 * step list can disagree with the server about what the steps are, and the
 * disagreement renders as confident progress. That refusal has not been
 * relaxed. What changed is that the number now ARRIVES.
 *
 * `GET /api/system/customers/:orgId/onboarding` sends `totalSteps`, computed by
 * the server as `checklist.length` — the length of the list it just built,
 * never a constant — so a fifteenth step added tomorrow moves the denominator
 * on both sides at once. That is the first source. `checklist.length` off a
 * payload that carried the list without the count is the second, and it is the
 * same fact measured on the console side of the same wire.
 *
 * The provisioning 201 has neither: no `checklist`, and therefore no
 * `totalSteps` for the server to take a length of. On that payload the console
 * still reports the numerator plus the server's own percentage and says the
 * total was not sent.
 *
 * DERIVING the total from the percentage remains refused: at n=1, p=7 the
 * arithmetic admits both 14 and 15, so it would be a guess dressed as a
 * measurement.
 */
export function onboardingProgress(
  onboarding: ServerOnboarding | null,
  checklistRead: PlatformRead,
): Measure<string> {
  if (!onboarding) return unserved(checklistRead);
  const done = onboarding.completedSteps.length;
  const total = onboarding.totalSteps ?? onboarding.checklist?.length;
  if (total !== undefined) return measured(`${done} / ${total}`);
  return measured(
    `${done} complete · ${onboarding.percentComplete}% (server-reported). The step total is ` +
      `not in this payload.`,
  );
}

export type StepState =
  | { readonly step: string; readonly state: 'complete'; readonly completedAt: Measure<string> }
  | { readonly step: string; readonly state: 'current' }
  | { readonly step: string; readonly state: 'incomplete' };

/**
 * THE STEPS THE SERVER NAMED, AND NOT ONE MORE.
 *
 * This is the guard against the failure the brief names: the console cannot
 * show a step complete that the server did not report, and it also cannot show
 * a step AT ALL that the server did not name. Both halves matter. A console
 * that listed fourteen hard-coded rows and ticked the ones in `completedSteps`
 * would be rendering thirteen assertions the server never made — every one of
 * them "incomplete", every one of them possibly wrong about what the steps even
 * are.
 *
 * When the server sends `checklist`, that IS the canonical list and it is used
 * in the server's order. When it does not, the console can name only what it
 * was told: the completed steps, plus the current step.
 */
export function stepStates(onboarding: ServerOnboarding): StepState[] {
  const completed = new Set(onboarding.completedSteps);

  const at = (step: string): Measure<string> => {
    const row = onboarding.checklist?.find((c) => c.step === step);
    if (!row) {
      return {
        kind: 'withheld',
        why:
          'the completion timestamp lives in the onboarding checklist, which this read does not ' +
          'carry',
      };
    }
    if (row.completedAt === null) {
      return { kind: 'absent', why: 'the server reports this step as having no completion time' };
    }
    return measured(row.completedAt);
  };

  const order: string[] = onboarding.checklist
    ? onboarding.checklist.map((c) => c.step)
    : [
        ...onboarding.completedSteps,
        ...(completed.has(onboarding.currentStep) ? [] : [onboarding.currentStep]),
      ];

  return order.map((step) => {
    if (completed.has(step)) return { step, state: 'complete', completedAt: at(step) };
    if (step === onboarding.currentStep) return { step, state: 'current' };
    return { step, state: 'incomplete' };
  });
}

/**
 * Live / Not Live.
 *
 * `isLive` is the server's own boolean (`completed.has('LIVE')`), so `false` is
 * a MEASUREMENT and renders as one. A null payload is not "not live" — it is a
 * question nobody answered.
 */
export function liveState(
  onboarding: ServerOnboarding | null,
  checklistRead: PlatformRead,
): Measure<string> {
  if (!onboarding) return unserved(checklistRead);
  return measured(onboarding.isLive ? 'Live' : 'Not live (server-reported)');
}

// ---------------------------------------------------------------------------
// The first administrator
// ---------------------------------------------------------------------------

export interface AdministratorReport {
  readonly state: Measure<string>;
  readonly invitationExpiresAt: Measure<string>;
  readonly deliveryJob: Measure<string>;
  readonly mailProvider: Measure<string>;
  readonly invitationLink: Measure<string>;
}

/**
 * `state` is `app_user.status` as the server reported it — `invited`, never
 * "created". The three lines under it are the difference between "the customer
 * has been emailed" and "nothing can email anybody", which is the blocking
 * condition an operator has to see BEFORE they tell a customer to check their
 * inbox.
 */
export function administratorReport(
  admin: {
    readonly state: string;
    readonly invitationExpiresAt: string;
    readonly delivery?: string;
    readonly provider?: string;
  },
  invitationUrl: string | undefined,
): AdministratorReport {
  return {
    state: measured(admin.state),
    invitationExpiresAt: measured(admin.invitationExpiresAt),
    deliveryJob: admin.delivery
      ? measured(admin.delivery)
      : { kind: 'absent', why: 'the reply carried no delivery outcome' },
    mailProvider: admin.provider
      ? measured(admin.provider)
      : { kind: 'absent', why: 'the reply carried no mail provider state' },
    invitationLink: invitationUrl
      ? measured(invitationUrl)
      : {
          kind: 'withheld',
          /**
           * WITHHELD AND NOT ABSENT, AND THE DIFFERENCE IS THE WHOLE POINT.
           * The invitation exists — `invitationId` is right there. The link is
           * a bearer credential for org_admin in a brand-new tenant and the
           * server does not put it in a reply outside development (Gate A /
           * H-8). Rendering a blank would tell the operator to go and make
           * another one.
           */
          why:
            'the invitation link is a bearer credential for org_admin and the platform API does ' +
            'not return it outside development. It goes to the administrator by email.',
        },
  };
}

/**
 * Is the customer actually reachable? The mail provider being unconfigured is
 * not a cosmetic detail: it means the invitation cannot be delivered and the
 * first administrator can never accept.
 */
export function provisioningBlockers(admin: {
  readonly delivery?: string;
  readonly provider?: string;
}): string[] {
  const blockers: string[] = [];
  if (admin.provider === 'not_configured') {
    blockers.push(
      'No mail provider is configured, so the first administrator’s invitation cannot be sent. ' +
        'Nobody can sign into this organization until one is.',
    );
  }
  if (admin.delivery && admin.delivery !== 'queued' && admin.delivery !== 'sent') {
    blockers.push(`The invitation delivery job reports "${admin.delivery}".`);
  }
  return blockers;
}
